import React, { memo } from "react"
import { Link } from "gatsby"
import { solutions } from "../../data/solutions"

/**
 * "Who we build for" — §6.3. The card leads with the problem as the largest
 * text, then the offer name with the audience alongside it, then the timeline.
 *
 * §6.3 originally specified the audience as an uppercase eyebrow ABOVE the
 * problem. It now sits inline after the offer name instead, on instruction:
 * "Installer Operations Platform — For fencing, fire protection & pool
 * installers" reads as one fact rather than two, and the problem gets the top
 * of the card.
 *
 * NO PRICE IN THIS SECTION (§2.7). The landing page carries no pricing at all;
 * the withheld price is what earns the click. Timelines only, via
 * `timelineShort` so the CTA never renders prose (§10).
 *
 * The card line states the BENEFIT, not the problem. Stating problems here
 * duplicated S1's job — the reader has already picked their symptom by the time
 * they reach this section, and repeating it told them nothing new.
 *
 * These lines are written copy, not drawn from solutions.js symptoms, which is
 * why `symptomIndex` is gone. NO METRIC OR PERCENTAGE belongs on these cards;
 * the numbers live in S3.
 *
 * Industry group headings are gone: the audience clause on the offer line now
 * carries that, which is what those headings were for.
 */
/**
 * Every card sits on the same ground. Five different resting tints made the set
 * read as five unrelated things, and once the section inverted onto $deep the
 * lightest of them read as selected or errored rather than as a sibling.
 *
 * The flagship is distinguished by construction instead, per patch §6.3's
 * asymmetry rule: it spans two columns and states its outcome at a larger step
 * than the other four. Emphasis comes from size and position, not from colour.
 */
const CARDS = [
  {
    id: "installer-platform",
    audience: "For fencing, fire protection & pool installers",
    benefit: "Quote faster, install sooner, and bill every change order you make.",
    wide: true, // §2.4 — the one deliberate asymmetry
  },
  {
    id: "odoo-manufacturers",
    audience: "For small & mid-sized manufacturers",
    benefit:
      "Know what a job costs before you quote it, and what stock you have before you promise it.",
  },
  {
    id: "practice-telehealth",
    audience: "For private practices & clinics",
    benefit: "Patients book themselves, arrive prepared, and turn up more often.",
  },
  {
    id: "ai-voice-receptionist",
    audience: "For home services, trades & clinics",
    benefit:
      "Every call answered, qualified and booked — including the ones at six on a Friday.",
  },
  {
    id: "fit-sprint",
    audience: "For any business choosing a system",
    benefit:
      // Tracks the Fit Sprint's timeline, which this card also renders as
      // "Live in {timelineShort}" straight from the data — the sentence and the
      // figure have to agree or the same card contradicts itself.
      "Know what to build, what it costs and how long it takes — in two weeks.",
  },
]

const cards = CARDS.map(c => {
  const s = solutions.find(x => x.id === c.id)
  if (!s) return null
  return {
    ...c,
    slug: s.slug,
    name: s.name,
    timelineShort: s.timelineShort,
  }
}).filter(Boolean)

const SolutionsGrid = () => (
  <section className="hs-grid">
    <div className="hs-grid__inner">
      <span className="hs-grid__eyebrow">What gets built</span>
      <h2 className="hs-grid__heading">
        What you get, and when you see it working
      </h2>
      {/* "on the work that carries a published date" is load-bearing — it keeps
          this sentence scoped to individual offers and clear of the rule against
          company-wide fixed-scope, fixed-price or fixed-timeline claims (§2.8).
          Do not cut it for brevity. */}
      <p className="hs-grid__lede">
        You get a written scope before anything starts, and on the work that
        carries a published date, a date you can hold us to.
      </p>

      <div className="hs-grid__cards">
        {cards.map((c, i) => (
          <Link
            key={c.slug}
            to={`/offers/${c.slug}`}
            className={
              "hs-grid__card" + (c.wide ? " hs-grid__card--wide" : "")
            }
            data-reveal
            style={{ transitionDelay: `${i * 80}ms` }}
          >
            <p className="hs-grid__problem">{c.benefit}</p>
            {/* Offer and audience are one line, not two blocks. The offer name
                keeps its size and weight and leads; the audience follows the
                dash at the same size in a lighter weight and colour. */}
            <p className="hs-grid__offer">
              {c.name}
              <span className="hs-grid__audience">
                {" — "}
                {c.audience}
              </span>
            </p>
            <span className="hs-grid__timeline">Live in {c.timelineShort}</span>
            <span className="hs-grid__cta">
              See what&rsquo;s included and what it costs &rarr;
            </span>
          </Link>
        ))}
      </div>

      {/* The open door moved out of this section — it is now its own block
          (components/home/OpenDoor.js) placed after ScrollServices, so the
          "broader than a package" step feeds straight into it. LANDING-PLAN §2. */}
    </div>
  </section>
)

export default memo(SolutionsGrid)
